import { useEffect, useState } from "react";

import { DataFilter } from "interfaces";

import Modal from "ui-components/Modal";
import Alert from "ui-components/Alert";
import Button from "ui-components/Button";
import Divider from "ui-components/Divider";
import InputField from "ui-components/Input-Field";
import FilterSummary from "ui-components/Data-Filters/Filter-Summary";

import {
  useGetFileColumns,
  useFetchDataFileStats,
} from "modules/data-files/api";
import FileFilters from "modules/data-files/elements/File-Filters";

import { BinaryOutcome } from "flows/driver-analysis/interfaces";
import { useGetInputFile, useOutcome, useDropNulls } from "./store";

const SetBinaryOutcome = () => {
  const currentFile = useGetInputFile();
  const stats = useFetchDataFileStats(currentFile);
  const columns = useGetFileColumns(currentFile);

  const [showModal, setShowModal] = useState(false);

  const [outcome, setOutcome] = useOutcome();

  const resetOutcome = () => setOutcome(null);

  const current = outcome;

  const [filters, setFilters] = useState<DataFilter.Filter[]>([]);
  const [variable, setVariable] = useState<string>();
  const [tagOnes, setTagOnes] = useState<string>("success");
  const [tagZeros, setTagZeros] = useState<string>("failure");
  const [view, setView] = useState<"filters" | "tags">("filters");

  useEffect(() => {
    return () => setView("filters");
  }, [setView]);

  const nullsPresent = outcome
    ? outcome.definition.filter((d) =>
        stats[d.column] ? stats[d.column].size > stats[d.column].count : false
      ).length >= 1
    : false;

  const [dropNulls, setDropNulls] = useDropNulls();

  const outer = (
    <div>
      {current ? (
        <>
          <div>
            <p>
              You have currently defined{" "}
              <span className="italic font-semibold">{current.variable}</span>{" "}
              as the outcome for this project. It has two classes:{" "}
              <span className="italic font-semibold">{current.tag_ones}</span>{" "}
              and{" "}
              <span className="italic font-semibold">{current.tag_zeros}</span>.
            </p>
            <p className="pt-2">
              <span className="italic font-semibold">{current.tag_ones}</span>{" "}
              would be rows meeting ALL the below conditions:
            </p>
          </div>

          <ul className="list-decimal ml-2 pl-2 list-inside">
            {current.definition.map((d, i) => (
              <li key={i} className="ml-2 pl-2">
                <FilterSummary filter={d} />
              </li>
            ))}
          </ul>
          <p>
            The remaining rows would be marked as{" "}
            <span className="italic font-semibold">{current.tag_zeros}</span>.
          </p>
          {nullsPresent && (
            <div className="pt-2 flex items-center">
              <input
                type="checkbox"
                checked={dropNulls}
                onClick={() => setDropNulls(!dropNulls)}
              />
              <div className="pl-2 ml-2">
                <p>
                  The columns you selected to define the outcome have some
                  invalid values. Do you want to{" "}
                  <span className="font-semibold underline">
                    drop rows with invalid values?
                  </span>{" "}
                  If you decide not to drop, all of them will be{" "}
                  <span className="font-semibold">
                    treated as "{current.tag_zeros}"
                  </span>
                </p>
              </div>
            </div>
          )}
          <p className="mt-2">
            You want to delete/reset outcome definition?{" "}
            <span
              onClick={() => {
                resetOutcome();
                setShowModal(true);
              }}
              className="uppercase text-primary-main cursor-pointer underline"
            >
              Click here
            </span>
          </p>
        </>
      ) : (
        <p className="text-center pt-2">
          <span
            className="underline text-primary-main cursor-pointer"
            onClick={() => setShowModal(true)}
          >
            Click here
          </span>{" "}
          to define the outcome.
        </p>
      )}
    </div>
  );

  const inner =
    view === "filters" ? (
      <div className="p-2 m-2">
        <FileFilters
          fileId={currentFile}
          title="Define Outcome Rules"
          filters={filters}
          updateFilters={(filters) => {
            setFilters(filters);
            setView("tags");
          }}
        />
        <Divider />
        <div className="text-right">
          <Button color="gray">Exit without Saving</Button>
        </div>
      </div>
    ) : (
      <div className="p-2 m-2">
        <InputField<string>
          type="text"
          name="variable"
          label="Outcome Name"
          required
          value={variable || ""}
          onChange={setVariable}
          help="What do you want to call this outcome? e.g., Success, Churn Indicator, etc."
        />
        <InputField<string>
          type="text"
          name="tag_ones"
          label="Tag for 1s"
          required
          value={tagOnes || ""}
          onChange={setTagOnes}
          help="What do you want to tag rows that MEET ALL CONDITIONS you defined here, e.g., churned, converted, successful, etc."
        />
        <InputField<string>
          type="text"
          name="tag_zeros"
          label="Tag for 0s"
          required
          value={tagZeros || ""}
          onChange={setTagZeros}
          help="What do you want to tag all OTHER ROWS? e.g., churned, converted, successful, etc."
        />

        {variable && columns.includes(variable) && (
          <Alert>
            {variable} already exists in data. You need to give it a unique
            name.
          </Alert>
        )}
        <Divider />
        <div className="mt-4 text-right">
          <Button color="gray" onClick={() => setShowModal(false)}>
            Exit without Saving
          </Button>
          <Button
            disabled={
              !variable ||
              columns.includes(variable) ||
              filters.length === 0 ||
              !tagOnes ||
              !tagZeros
            }
            onClick={() => {
              const outcome: BinaryOutcome = {
                type: "binary",
                variable: variable as string,
                definition: filters,
                tag_ones: tagOnes,
                tag_zeros: tagZeros,
              };
              setOutcome(outcome);
              setView("filters");
              setFilters([]);
              setShowModal(false);
            }}
          >
            Set Outcome Variable
          </Button>
        </div>
      </div>
    );

  return (
    <div className="flex">
      <div className="w-1/4">
        <p className="font-bold">2. Define the outcome you want to analyze:</p>
      </div>
      <div className="w-full">
        {outer}
        <Modal
          show={showModal}
          handleClose={() => setShowModal(false)}
          id="define-binary-outcomes"
          ariaLabel="define-binary-outcomes"
        >
          {inner}
        </Modal>
      </div>
    </div>
  );
};

export default SetBinaryOutcome;
